<template>
  <div>
    <SectionContainer>
      <ContainerMedium>
        <BreadCrumb :crumb="crumb" />

        <PrimaryTitle class="title">Política de Privacidade — Dark Channel Generator</PrimaryTitle>
        <p class="subtitle">Última atualização: 19 de fevereiro de 2026 &nbsp;·&nbsp; Versão da extensão: 1.2.0</p>

        <hr>

        <PrimaryParagraph class="mb-4">
          <ParagraphTitle class="mb-4">1. Introdução</ParagraphTitle>
          <PrimaryParagraph>
            O <strong>Dark Channel Generator</strong> ("Extensão", "nós", "nosso") respeita sua privacidade e está comprometido em proteger seus dados pessoais. Esta Política de Privacidade explica como coletamos, usamos e protegemos suas informações ao usar nossa extensão do Chrome.
          </PrimaryParagraph>
        </PrimaryParagraph>

        <PrimaryParagraph class="mb-4">
          <ParagraphTitle class="mb-4">2. Informações que Coletamos</ParagraphTitle>

          <PrimaryParagraph class="mb-3">
            <strong>2.1 Dados de Autenticação</strong>
          </PrimaryParagraph>

          <PrimaryParagraph class="mb-3">
            <u>O que coletamos:</u>
            <ul>
              <li>Cookie de autenticação <code>cc_legacy_token</code> do domínio Cyberclass;</li>
              <li>Informações básicas do perfil (nome, e-mail, avatar) obtidas da API do Cyberclass;</li>
              <li>Status da assinatura (ativo, cancelado, expirado).</li>
            </ul>
          </PrimaryParagraph>

          <PrimaryParagraph class="mb-3">
            <u>Por que coletamos:</u>
            <ul>
              <li>Para verificar se você possui uma assinatura ativa do Cyberclass;</li>
              <li>Para personalizar a interface com seu nome e foto;</li>
              <li>Para controlar acesso aos recursos da extensão.</li>
            </ul>
          </PrimaryParagraph>

          <PrimaryParagraph class="mb-3">
            <u>Como coletamos:</u>
            <ul>
              <li>Lemos o cookie de autenticação armazenado em seu navegador quando você acessa o Cyberclass;</li>
              <li>Fazemos requisições autenticadas à API do Cyberclass.</li>
            </ul>
          </PrimaryParagraph>

          <PrimaryParagraph class="mb-3">
            <strong>2.2 Dados de Uso da Extensão</strong>
          </PrimaryParagraph>

          <PrimaryParagraph class="mb-3">
            <u>O que coletamos localmente:</u>
            <ul>
              <li>Configurações da extensão (tamanho de lote, intervalos, pasta de downloads);</li>
              <li>Fila de prompts em processamento;</li>
              <li>Estatísticas de vídeos (enviados, gerados, falhas, downloads);</li>
              <li>Cache do status de autenticação (válido por 5 minutos).</li>
            </ul>
          </PrimaryParagraph>

          <PrimaryParagraph>
            Todos esses dados são armazenados <strong>localmente</strong> em seu navegador usando a Chrome Storage API. Eles <strong>nunca</strong> são enviados para servidores externos e sua atividade no Google Veo jamais é rastreada.
          </PrimaryParagraph>
        </PrimaryParagraph>

        <PrimaryParagraph class="mb-4">
          <ParagraphTitle class="mb-4">3. Como Usamos Suas Informações</ParagraphTitle>

          <PrimaryParagraph class="mb-3">
            <strong>3.1 Controle de Acesso</strong><br>
            Verificamos se você possui assinatura ativa antes de permitir o uso da extensão e exibimos mensagens apropriadas quando a assinatura expira ou é cancelada.
          </PrimaryParagraph>

          <PrimaryParagraph class="mb-3">
            <strong>3.2 Personalização</strong><br>
            Utilizamos seu nome e avatar para exibição na interface, além de manter suas preferências de configuração entre sessões.
          </PrimaryParagraph>

          <PrimaryParagraph>
            <strong>3.3 Funcionalidade</strong><br>
            Gerenciamos a fila de prompts, automatizamos o envio e download de vídeos e mantemos estatísticas de uso localmente.
          </PrimaryParagraph>
        </PrimaryParagraph>

        <PrimaryParagraph class="mb-4">
          <ParagraphTitle class="mb-4">4. Compartilhamento de Dados</ParagraphTitle>

          <PrimaryParagraph class="mb-3">
            <strong>NÓS NÃO:</strong>
            <ul>
              <li>Vendemos seus dados pessoais;</li>
              <li>Compartilhamos suas informações com terceiros;</li>
              <li>Enviamos dados de uso para servidores de analytics;</li>
              <li>Rastreamos sua navegação fora do escopo da extensão;</li>
              <li>Acessamos dados de outros sites além dos necessários.</li>
            </ul>
          </PrimaryParagraph>

          <PrimaryParagraph>
            <strong>Comunicação com servidores (apenas o necessário):</strong>
            <ul>
              <li><strong>API Cyberclass</strong> — somente para autenticação;</li>
              <li><strong>Google Veo</strong> — funcionalidade nativa da página.</li>
            </ul>
            Todas as comunicações são criptografadas via HTTPS.
          </PrimaryParagraph>
        </PrimaryParagraph>

        <PrimaryParagraph class="mb-4">
          <ParagraphTitle class="mb-4">5. Armazenamento e Segurança</ParagraphTitle>

          <PrimaryParagraph class="mb-3">
            <strong>5.1 Armazenamento Local</strong><br>
            Os dados são armazenados localmente em seu dispositivo. Você pode limpá-los a qualquer momento pelas configurações do Chrome. Os dados não são sincronizados com outros dispositivos sem seu consentimento.
          </PrimaryParagraph>

          <PrimaryParagraph class="mb-3">
            <strong>5.2 Cache de Autenticação</strong><br>
            O status de autenticação é armazenado em cache por 5 minutos, sendo automaticamente invalidado se os dados estiverem corrompidos. Você pode forçar uma nova verificação clicando em "Verificar Novamente".
          </PrimaryParagraph>

          <PrimaryParagraph>
            <strong>5.3 Segurança</strong><br>
            O token de autenticação é transmitido exclusivamente via HTTPS. Não armazenamos senhas nem temos acesso a informações bancárias ou de pagamento.
          </PrimaryParagraph>
        </PrimaryParagraph>

        <PrimaryParagraph class="mb-4">
          <ParagraphTitle class="mb-4">6. Cookies e Tecnologias Similares</ParagraphTitle>

          <PrimaryParagraph class="mb-3">
            <strong>6.1 Cookie que lemos</strong>
            <ul>
              <li><strong>Nome:</strong> <code>cc_legacy_token</code></li>
              <li><strong>Origem:</strong> cyberclass.com.br</li>
              <li><strong>Tipo:</strong> Cookie de autenticação</li>
              <li><strong>Propósito:</strong> Verificar identidade e status de assinatura</li>
              <li><strong>Acesso:</strong> Somente leitura — não modificamos o cookie</li>
            </ul>
          </PrimaryParagraph>

          <PrimaryParagraph>
            <strong>6.2 Não criamos cookies</strong><br>
            A extensão não cria nem modifica cookies. Realizamos apenas a leitura do cookie existente, criado pelo Cyberclass.
          </PrimaryParagraph>
        </PrimaryParagraph>

        <PrimaryParagraph class="mb-4">
          <ParagraphTitle class="mb-4">7. Seus Direitos</ParagraphTitle>

          <PrimaryParagraph class="mb-3">
            <strong>7.1 Acesso</strong><br>
            Você pode visualizar quais dados estão armazenados localmente via Chrome DevTools &gt; Application &gt; Storage.
          </PrimaryParagraph>

          <PrimaryParagraph class="mb-3">
            <strong>7.2 Exclusão</strong><br>
            Remover a extensão apaga todos os dados armazenados. Para limpar manualmente: Chrome → Configurações → Extensões → Dark Channel Generator → Remover dados.
          </PrimaryParagraph>

          <PrimaryParagraph class="mb-3">
            <strong>7.3 Portabilidade</strong><br>
            Os dados de configuração podem ser exportados manualmente pelas configurações da extensão.
          </PrimaryParagraph>

          <PrimaryParagraph>
            <strong>7.4 Revogação</strong><br>
            Desinstalar a extensão revoga todas as permissões. Fazer logout do Cyberclass invalida a autenticação imediatamente.
          </PrimaryParagraph>
        </PrimaryParagraph>

        <PrimaryParagraph class="mb-4">
          <ParagraphTitle class="mb-4">8. Permissões da Extensão</ParagraphTitle>

          <PrimaryParagraph class="mb-3">
            <strong>8.1 Permissões solicitadas e suas finalidades:</strong>
            <ul>
              <li><code>cookies</code> — Ler o token de autenticação;</li>
              <li><code>storage</code> — Armazenar configurações localmente;</li>
              <li><code>tabs</code> — Gerenciar abas do Google Veo;</li>
              <li><code>downloads</code> — Auto-download dos vídeos gerados;</li>
              <li><code>system.display</code> — Posicionar a janela na tela.</li>
            </ul>
          </PrimaryParagraph>

          <PrimaryParagraph>
            <strong>8.2 Domínios com acesso:</strong>
            <ul>
              <li><code>labs.google/*</code> — Página do Google Veo (funcionalidade principal);</li>
              <li><code>cyberclass.com.br/*</code> — Verificação de autenticação;</li>
            </ul>
            Cada permissão é estritamente necessária para uma funcionalidade específica.
          </PrimaryParagraph>
        </PrimaryParagraph>

        <PrimaryParagraph class="mb-4">
          <ParagraphTitle class="mb-4">9. Privacidade de Crianças</ParagraphTitle>
          <PrimaryParagraph>
            Esta extensão não é direcionada a menores de 13 anos. Não coletamos intencionalmente dados de crianças.
          </PrimaryParagraph>
        </PrimaryParagraph>

        <PrimaryParagraph class="mb-4">
          <ParagraphTitle class="mb-4">10. Alterações nesta Política</ParagraphTitle>
          <PrimaryParagraph>
            Podemos atualizar esta política ocasionalmente. Alterações significativas serão comunicadas por meio de atualização da extensão com changelog, aviso na interface ou pela data de "Última atualização" no topo deste documento.
          </PrimaryParagraph>
        </PrimaryParagraph>

        <PrimaryParagraph class="mb-4">
          <ParagraphTitle class="mb-4">11. Conformidade com Leis</ParagraphTitle>
          <PrimaryParagraph>
            Esta extensão está em conformidade com a <strong>Lei Geral de Proteção de Dados (LGPD)</strong> — Lei nº 13.709/2018 —, o <strong>GDPR</strong> (Regulamento Geral de Proteção de Dados da União Europeia) e as <strong>Chrome Web Store Developer Program Policies</strong>.
          </PrimaryParagraph>
        </PrimaryParagraph>

        <PrimaryParagraph class="mb-4">
          <ParagraphTitle class="mb-4">12. Contato</ParagraphTitle>
          <PrimaryParagraph>
            Para questões sobre privacidade, entre em contato:<br>
            <strong>E-mail:</strong> suporte@cyberclass.com.br<br>
            <strong>Website:</strong> <router-link :to="{ name: 'home' }">cyberclass.com.br</router-link><br>
            <strong>Desenvolvedor:</strong> Dark Channel / Cyberclass
          </PrimaryParagraph>
        </PrimaryParagraph>

        <PrimaryParagraph class="mb-4">
          <ParagraphTitle class="mb-4">13. Transparência</ParagraphTitle>
          <PrimaryParagraph>
            Todo o código-fonte da extensão está disponível para inspeção no Chrome. Coletamos apenas o mínimo necessário, com total transparência sobre o uso dos dados e respeito à sua privacidade em primeiro lugar.
          </PrimaryParagraph>
        </PrimaryParagraph>

        <hr>

        <PrimaryParagraph class="mb-4 summary-box">
          <ParagraphTitle class="mb-3">Resumo</ParagraphTitle>
          <PrimaryParagraph class="mb-2">
            <strong>O que fazemos:</strong>
            <ul>
              <li>Lemos seu cookie de login do Cyberclass para verificar sua assinatura;</li>
              <li>Salvamos suas configurações localmente no navegador.</li>
            </ul>
          </PrimaryParagraph>
          <PrimaryParagraph>
            <strong>O que NÃO fazemos:</strong>
            <ul>
              <li>Rastrear sua navegação;</li>
              <li>Enviar dados para servidores externos;</li>
              <li>Compartilhar informações com terceiros;</li>
              <li>Vender ou acessar dados sensíveis.</li>
            </ul>
          </PrimaryParagraph>
        </PrimaryParagraph>

        <PrimaryParagraph>
          <strong>Dark Channel / Cyberclass</strong>
        </PrimaryParagraph>
      </ContainerMedium>
    </SectionContainer>
  </div>
</template>

<script>
  import PrimaryTitle from '@/components/text/titles/PrimaryTitle'
  import ParagraphTitle from '@/components/text/titles/ParagraphTitle'
  import ContainerMedium from '@/components/layout/containers/ContainerMedium'
  import SectionContainer from '@/components/layout/SectionContainer'
  import PrimaryParagraph from '@/components/text/paragraphs/PrimaryParagraph'
  import BreadCrumb from '@/components/layout/BreadCrumb'

  export default {
    components: {
      ContainerMedium,
      PrimaryTitle,
      SectionContainer,
      PrimaryParagraph,
      ParagraphTitle,
      BreadCrumb,
    },

    metaInfo () {
      return { title: 'Política de Privacidade — Dark Channel Generator | CyberClass' }
    },

    computed: {
      crumb () {
        return [
          {
            label: 'Página Inicial',
            route: { name: 'home' }
          },
          {
            label: 'Políticas de Privacidade',
            route: { name: 'legal.privacy' }
          },
          {
            label: 'Dark Channel Generator'
          }
        ]
      }
    }
  }
</script>

<style scoped lang="scss">
  @import "~@/assets/sass/variables";

  .title {
    margin: 4px 0;
  }

  .subtitle {
    font-size: 0.89rem;
  }

  section:not(:last-child) {
    margin-bottom: 30px;
    padding-bottom: 30px;
    border-bottom: 1px solid rgba(#000, .2);
  }

  ul {
    padding-left: 20px;

    li { list-style: disc; }
  }

  code {
    font-size: 0.85em;
    background: rgba(#000, .07);
    padding: 1px 5px;
    border-radius: 3px;
    color: $white300;
  }

  .summary-box {
    position: relative;
    padding: 1rem;

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 1px;
      background: linear-gradient(158deg, $highlightPrimaryColor 4.86%, $red100 52.82%, $purple200 100.77%);
    }
  }
</style>
